import type { IExpressData, ProviderInfoItem, TSupportedAddressTypes } from './express'
import type { IAddress } from './address'
import { CUSTOM_FREIGHT_REASON_ENUM, ENUM_TYPES } from '../enum'
import { ExpressTypeConfigVo } from '@/services/api/type'

export * from './express'
export * from './address'

type IOrderInfo = {
  paymentType: number
  codAmount: number

  isECommerce?: boolean
  /** 订单运费 */
  salesOrderFreight?: number
  /** 订单 cod amount */
  salesOrderAmount?: number
  // 接口返回的初始订单币种
  salesOrderCurrencyCode?: string
}

/**
 * 发货国家
 */
export type ISenderAddressName = {
  departure_countryCode?: string
  departure_province?: string
  departure_city?: string
  departure_district?: string
  departure_zipCode?: string
  departure_town?: string
  departure_address?: string
  departure_addressTwo?: string
  departure_fullName?: string
  departure_firstName?: string
  departure_lastName?: string
  departure_mobile?: string
  departure_email?: string
}

/**
 * 收货国家
 */
export type IConsigneeAddressName = {
  // 收货国家
  arrival_countryCode?: string

  /**
   * store's code
   */
  arrival_station_code?: string
  /** store's type */
  arrival_station_type?: number

  /** 收货省 */
  arrival_province?: string
  /** 收货店配省 */
  arrival_store_province?: string
  arrival_zipCode?: string

  /** 收货市 */
  arrival_city?: string
  /** 收货店配市 */
  arrival_store_city?: string

  /** 收货地区 */
  arrival_district?: string
  /** 收货店配区 */
  arrival_store_district?: string
  arrival_address?: string
  arrival_addressTwo?: string
  /** 收货4级地址 */
  arrival_town?: string
  arrival_fullName?: string
  arrival_firstName?: string
  arrival_lastName?: string
  arrival_mobile?: string
}

export type IProviderAddressVO = ISenderAddressName & IConsigneeAddressName

export type IExpressProps = {
  /**
   * 发货，收货地址
   */
  addressVO?: Partial<IProviderAddressVO>
  /**
   * 支持的地址类型
   * HOME_STREET STORE_STREET
   */
  supportedAddressTypes?: TSupportedAddressTypes[]

  /**
   * 温层
   */
  supportStorageCdts?: any[]
  /**
   * 服务产品编码
   */
  companyCode?: string

  /**
   * 币种
   */
  currencyCode?: string

  /**
   * 千点取需求 https://shopline.yuque.com/mlu41d/gy9ldi/ylnslw
   * 传入的是门店的类型
   */
  stationSeq?: string

  /** 重量 */
  parcel_weight?: string | number
  /** 单位 */
  parcel_weight_unit?: string
  /** 长宽高 */
  parcel_length?: string | number
  parcel_width?: string | number
  parcel_height?: string | number

  /**
   * 1 宅配
   * 2 店配
   */
  arrival_method?: number

  /**
   * 订单转运单，需要同步过来的订单信息
   */
  orderInfo?: IOrderInfo

  /**
   * 订单来源标志
   */
  appId?: string

  /**
   * 是否需要门店 type
   */
  needStationType?: boolean

  /** 是否自动匹配的服务产品 */
  isAutoMatch?: boolean

  /** 服务产品编码 */
  expressTypeCode?: string
  /** 订单COD币种 */
  codCurrencyCode?: string

  /** 计算不出自定义运费的情况 */
  osFreightErrorCode?: `${CUSTOM_FREIGHT_REASON_ENUM}`

  /** 扣减运费 */
  deductedFreight?: number
  deductedFreightCurrencyCode?: string
  /** 订单原始cod币种 */
  apiCodCurrencyCode?: string
  /** 订单原始cod金额 */
  apiCodAmount?: number
}

/** 代收货款 */
export enum COD_TYPE_ENUM {
  /** 使用cod */
  cod = 1,
  /** 不使用cod */
  notCod = 2,
}

export type IProviderFormValues = {
  /**
   * 是否使用 cod
   * 1 cod
   * 2 其他
   */
  provider_payment_type?: number

  /**
   * cod 金额
   */
  provider_cod_amount?: number | string

  /**
   *  发货方式
   *  pickUp:上门取货/预约揽收 = 1,
   *  dropOff: 自邮 = 2,
   */
  provider_pickup_type?: number

  /**
   * 自寄门店
   */
  provider_pickup_station_code?: string

  /**
   * 支付方式
   */
  provider_pay_type?: number

  /**
   * 月结账号
   */
  provider_monthly_account?: string

  /**
   * 月结子账号
   */
  provider_channel_seller_id?: string

  /**
   * 证件类型
   */
  provider_consignee_id_type?: number

  /**
   * 证件号
   */
  provider_consignee_id_num?: string

  /**
   * 税号类型
   */
  provider_tax_type?: string

  /**
   * 税号
   */
  provider_tax_no?: string

  /**
   * EORI号码
   */
  provider_custom_id_no?: string

  /**
   * 收件人姓名
   */
  provider_consignee_name?: string

  /** 揽收时间 */
  provider_pickup_dates?: string[]
  provider_delivery_dates?: string[]

  provider_package_desc?: string
  provider_hazmat_type_code?: string
  provider_invoice_no?: string
  provider_invoice_date?: any
  provider_invoice_path?: string
  provider_message_trace_notify_status?: string[]
  provider_custom_freight_currency_code?: string

  provider_custom_freight?: number

  provider_taxationCurrencyCode?: string
  provider_taxation?: number
  /** 增值服务 */
  provider_service_types?: string[]
}

export interface IServiceProviderVO {
  /** 服务产品录入信息配置( 税号/身份信息/收件人名称) */
  providerExtraConfig?: ExpressTypeConfigVo[]
  providerDateOptions?: ProviderInfoItem[]
  providerTimeOptions?: ProviderInfoItem['timeIntervals']
  /**是否支持到店自寄时选择门店 */
  supportStationPickUp?: boolean
  providerPaymentOptions?: Array<{ label: string; value: any }>

  provider?: IExpressData

  /**
   * 记录上一次的 COD 金额
   */
  previousCodFee?: number | string
  /**
   * 记录上一次的 COD 选项
   */
  previousShipmentServiceType?: number

  /**
   * 子账号 option
   */
  subAccountOption?: {
    value: string
    label: null | string
  }[]

  /**
   * 是否是 HK kerry 到 台湾发货
   */
  isKerryHk2TW?: boolean

  props?: IExpressProps

  list?: IExpressData[]
  listLoading?: boolean

  /**
   * normal 单个/普通
   * batch 批量场景
   * merge 合单发货场景
   */
  type: `${ENUM_TYPES}`

  tab: string

  /**
   * 筛选
   */
  filterArr: string[]

  /** 派送时间 */
  providerDeliveryDateOptions?: ProviderInfoItem[]
  providerDeliveryTimeOptions?: ProviderInfoItem['timeIntervals']

  popupVisible?: boolean
}

export type ICoverAddressTypes = Pick<
  IAddress,
  | 'senderCountryCode'
  | 'senderCity'
  | 'senderProvince'
  | 'senderDistrict'
  | 'senderDistrict'
  | 'senderZipCode'
  | 'senderAddress'
  | 'senderAddressTwo'
  | 'senderTown'
  | 'senderName'
  | 'senderMobile'
  | 'consigneeCountryCode'
  | 'consigneeCity'
  | 'consigneeDistrict'
  | 'consigneeProvince'
  | 'consigneeZipCode'
  | 'consigneeAddress'
  | 'consigneeAddressTwo'
  | 'consigneeTown'
  | 'consigneeName'
  | 'consigneeMobile'
>

export type ISetProviderProps = Partial<IExpressProps & { cover?: boolean } & Pick<IServiceProviderVO, 'type' | 'previousShipmentServiceType'>>
